import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { SignupForm } from '@/components/auth/SignupForm';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { getPostAuthRedirect, buildAuthSwitchUrl, buildWelcomeUrl } from '@/lib/auth-intent';
import { needsProfile } from '@/lib/onboarding-profile';

export function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup, isAuthenticated } = useAuth();
  const [error, setError] = useState<string>();

  // New users go through the one-time profiling step; returning/complete users skip it.
  // Preserve redirect/plan intent across /welcome so post-profiling lands correctly.
  const postAuthTarget = () =>
    needsProfile(useAuthStore.getState().user)
      ? buildWelcomeUrl(searchParams)
      : getPostAuthRedirect(searchParams);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(postAuthTarget(), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, searchParams]);

  const handleSubmit = async (data: { name: string; email: string; password: string; organization_name: string }) => {
    try {
      await signup(data);
      navigate(postAuthTarget(), { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create account');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Brand pane */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-card border-r border-border relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,hsl(var(--primary)/0.10),transparent_55%)] pointer-events-none" />
        <div className="relative z-10">
          <span className="text-2xl font-bold tracking-tight text-foreground">gateco</span>
        </div>
        <div className="relative z-10 space-y-8">
          <div className="space-y-3">
            <h1 className="text-4xl font-semibold tracking-tight leading-tight">Start gating<br/>in minutes.</h1>
            <p className="text-muted-foreground max-w-sm text-sm leading-relaxed">Connect a vector database, sync an identity provider, and enforce your first policy, all before lunch.</p>
          </div>
          <ul className="space-y-3 text-sm text-muted-foreground">
            {['Free tier with 1 connector and 1K retrievals', 'No credit card required to get started', 'Full policy engine from day one'].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative z-10 text-xs text-muted-foreground">Questions? support@gateco.ai</p>
      </div>

      {/* Form pane */}
      <div className="flex w-full lg:w-1/2 flex-col items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm space-y-6">
          <div className="lg:hidden text-center">
            <Link to="/" className="text-2xl font-bold tracking-tight text-foreground">gateco</Link>
          </div>
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Create an account</h2>
            <p className="text-sm text-muted-foreground mt-1">Free tier, no credit card required</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
            <SignupForm onSubmit={handleSubmit} error={error} />
            <p className="mt-4 text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to={buildAuthSwitchUrl('login', searchParams)} className="text-primary hover:underline">Log in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
